/*
  ESTA FOLHA É CÓPIA DO NOTEBOOK.

  Origem: ../adsa-financas/src/shared/datas.ts

  **Por que copiar em vez de reescrever:** este arquivo já estava duplicado à mão neste
  repositório, e duplicação à mão de cargo, permissão, data e dinheiro é defeito que aparece como
  "o pastor não entra" ou "o número do painel não bate com o do programa". Fase 3 do espelho,
  pedida pelo Cristiano em 12/10/2026: compartilhar só o que o painel já copiava à mão, **sem
  reorganizar o notebook**.

  **MEXEU LÁ, MEXA AQUI.** E há prova disso em `testes/copia-do-notebook.test.ts`: ela lê os dois
  arquivos e fica vermelha quando o que está abaixo da marca deixa de ser igual ao original.

  **O `@ts-nocheck` saiu em 13/10/2026, e isso é notícia boa.** Este repositório liga quatro
  travas de compilador (`noUncheckedIndexedAccess`, `exactOptionalPropertyTypes`,
  `isolatedModules`, `verbatimModuleSyntax`), e até a entrega 2.11 o notebook não as ligava: a
  cópia exata dava 19 erros só por causa delas, e consertá-los aqui quebraria a exatidão, que é a
  coisa toda que esta cópia existe para ter. Então o arquivo entrava com `@ts-nocheck`, e um erro
  de tipo dentro dele não era visto aqui.

  A sessão do notebook ligou as mesmas quatro travas lá, e agora a cópia exata compila inteira,
  com tudo ligado. **Se um dia ela voltar a não compilar, não ponha `@ts-nocheck` de volta sem
  pensar**: o que isso quer dizer é que os dois lados se separaram de novo, e o conserto é lá.

  Nada daqui para baixo se edita à mão: copie o arquivo do notebook inteiro por cima e rode a
  prova.
*/
/* ==== FIM DO CABECALHO DA COPIA: daqui para baixo e copia exata do notebook ==== */
/**
 * Data e hora — um lugar só, para o sistema inteiro.
 *
 * O banco guarda instantes em **UTC**: é o que o SQLite escreve em `datetime('now')` e o
 * que `agora()` produz. Isso não muda e não deve mudar — data guardada em UTC é
 * comparável, ordenável em texto puro e continua certa se um dia o computador mudar de
 * fuso ou o sistema for sincronizado com a sede.
 *
 * Só que o computador da tesouraria está em Brasília, três horas atrás de UTC. Um dízimo
 * lançado às 22:00 do dia 31 fica guardado como 01:00 do dia 1º. Guardar assim está
 * certo; MOSTRAR assim está errado — e a trilha de auditoria é prova, tem que dizer a
 * hora que a pessoa viu no relógio dela.
 *
 * Por isso a conversão acontece em UM lugar: aqui. Toda tela, todo PDF e toda planilha
 * que mostra data e hora chama `formatarDataHora`. Conversão espalhada é como se erra
 * fuso horário: um canto converte, o outro esquece, e ninguém sabe mais qual dos dois
 * está mentindo.
 *
 * A outra metade da regra: **"hoje" é sempre o hoje do relógio da parede.** A data que
 * já vem digitada num lançamento, o nome do arquivo de backup e a pergunta "já houve
 * cópia hoje?" usam a data LOCAL. Se usassem UTC, o dia viraria às 21h — e quem lançasse
 * a oferta do culto da noite veria o sistema dizer que já é amanhã.
 */

/** "AAAA-MM-DD HH:MM" ou "AAAA-MM-DD HH:MM:SS", com espaço ou T. Data pura não casa. */
const INSTANTE = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/

const dd = (n: number): string => String(n).padStart(2, '0')

function comoTexto(d: Date): string {
  return `${d.getFullYear()}-${dd(d.getMonth() + 1)}-${dd(d.getDate())}` +
    ` ${dd(d.getHours())}:${dd(d.getMinutes())}:${dd(d.getSeconds())}`
}

/**
 * O instante guardado em UTC, no fuso do computador: "AAAA-MM-DD HH:MM:SS".
 *
 * Uma data pura ("2026-08-31") volta como veio — é dia de calendário, não instante:
 * a data de uma saída não tem hora e não pode andar para trás ao ser exibida.
 */
export function paraLocal(quandoUTC: string): string {
  const p = INSTANTE.exec(quandoUTC)
  if (!p) return quandoUTC
  const emUTC = Date.UTC(Number(p[1]), Number(p[2]) - 1, Number(p[3]), Number(p[4]), Number(p[5]), p[6] ? +p[6] : 0)
  return comoTexto(new Date(emUTC))
}

interface Opcoes {
  /** O que aparece quando não há data. Padrão: um traço. */
  vazio?: string
  /** "31/08/2026 às 22:00" em vez de "31/08/2026 22:00". */
  comAs?: boolean
}

/**
 * Data e hora como a pessoa lê: "31/08/2026 22:00".
 *
 * **É esta a função que toda tela usa.** Recebe o que veio do banco (UTC) e devolve o
 * horário do relógio da parede.
 */
export function formatarDataHora(
  quandoUTC: string | null | undefined,
  opcoes: Opcoes = {}
): string {
  const { vazio = '—', comAs = false } = opcoes
  if (!quandoUTC) return vazio

  const [data = '', hora] = paraLocal(quandoUTC).split(' ')
  const dia = data.split('-').reverse().join('/')
  if (!hora) return dia
  return `${dia}${comAs ? ' às ' : ' '}${hora.slice(0, 5)}`
}

/** Só o dia: "31/08/2026". Aceita tanto uma data pura quanto um instante. */
export function formatarData(
  quando: string | null | undefined,
  opcoes: Pick<Opcoes, 'vazio'> = {}
): string {
  const { vazio = '—' } = opcoes
  if (!quando) return vazio
  const [dia = ''] = paraLocal(quando).split(' ')
  return dia.split('-').reverse().join('/')
}

/** A data de hoje no relógio da parede: "AAAA-MM-DD". */
export function hojeISO(): string {
  const d = new Date()
  return `${d.getFullYear()}-${dd(d.getMonth() + 1)}-${dd(d.getDate())}`
}

/** Agora, no relógio da parede: "AAAA-MM-DD HH:MM:SS". Para as regras de "hoje". */
export function agoraLocal(): string {
  return comoTexto(new Date())
}

/** O mês de hoje: "AAAA-MM". */
export function competenciaAtual(): string {
  return hojeISO().slice(0, 7)
}

/** O dia 1º do mês de hoje: "AAAA-MM-01". */
export function primeiroDiaDoMes(): string {
  return `${competenciaAtual()}-01`
}

/**
 * O começo do dia LOCAL, escrito em UTC — para comparar com o que está guardado.
 *
 * O filtro da auditoria é o caso: a pessoa escolhe "31/08" pensando no dia dela, e o
 * banco guarda em UTC. Comparar o texto direto deixaria de fora tudo o que aconteceu
 * depois das 21h daquele dia, que no banco já está gravado como o dia seguinte —
 * justamente o culto da noite.
 */
export function comecoDoDiaEmUTC(dataLocal: string): string {
  const [a = NaN, m = NaN, d = NaN] = dataLocal.split('-').map(Number)
  return new Date(a, m - 1, d, 0, 0, 0).toISOString().slice(0, 19).replace('T', ' ')
}

/** O fim do dia LOCAL (23:59:59), escrito em UTC. O par de `comecoDoDiaEmUTC`. */
export function fimDoDiaEmUTC(dataLocal: string): string {
  const [a = NaN, m = NaN, d = NaN] = dataLocal.split('-').map(Number)
  return new Date(a, m - 1, d, 23, 59, 59).toISOString().slice(0, 19).replace('T', ' ')
}

const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
]

/** "2026-07" → "julho": o mês sozinho, para a frase que já está falando daquele mês (0.3.25). */
export function nomeDoMes(competencia: string): string {
  return MESES[Number(competencia.slice(5, 7)) - 1] ?? competencia
}

/** "2026-12" → "dezembro de 2026". A mesma frase na tela, no papel e nas mensagens. */
export function competenciaPorExtenso(competencia: string): string {
  const [ano = NaN, mes = NaN] = competencia.split('-').map(Number)
  return `${MESES[mes - 1]} de ${ano}`
}

/**
 * Aviso discreto da data fora do mês da competência (Etapa 12.4c): "02/10/2026" no Fluxo de setembro dá
 * "fora de setembro". Só avisa — o pagamento pode mesmo ter caído noutro mês. Nulo quando está no mês.
 */
export function avisoDeDataForaDoMes(data: string | null, competencia: string): string | null {
  if (!data || data.slice(0, 7) === competencia) return null
  return `fora de ${MESES[Number(competencia.slice(5, 7)) - 1]}`
}

/** O último dia de um mês: "2026-09" vira "2026-09-30". */
export function ultimoDiaDoMes(competencia: string): string {
  const [ano = NaN, mes = NaN] = competencia.split('-').map(Number)
  return `${competencia}-${dd(new Date(ano, mes, 0).getDate())}`
}

/**
 * A data com que o campo "Data" nasce preenchido, numa tela que é de um mês (decisão do Cristiano,
 * 05/10/2026).
 *
 * No mês corrente é HOJE, que é o dia de quem está lançando. Num mês que já passou é o ÚLTIMO DIA
 * daquele mês: quem abre "Saídas de setembro" no dia 5 de outubro está acertando setembro, e o que
 * ele lançar tem que aparecer na folha que ele está vendo. Antes nascia hoje sempre, e a saída caía
 * no mês seguinte, sumindo da lista sem dizer nada a ninguém.
 *
 * Mês que ainda não chegou também nasce em hoje: a tela não deixa escolher data no futuro.
 */
export function dataPadraoDoMes(competencia: string, hoje = hojeISO()): string {
  if (!/^\d{4}-\d{2}$/.test(competencia)) return hoje
  return competencia >= hoje.slice(0, 7) ? hoje : ultimoDiaDoMes(competencia)
}

/** O mês vizinho: "2026-01" com passo -1 vira "2025-12". */
export function mesVizinho(competencia: string, passo: number): string {
  const [ano = NaN, mes = NaN] = competencia.split('-').map(Number)
  const d = new Date(ano, mes - 1 + passo, 1)
  return `${d.getFullYear()}-${dd(d.getMonth() + 1)}`
}
